/**
 * AdvancedSettings Component
 *
 * Collapsible section for advanced image generation parameters.
 * Includes sliders for guidance scale, steps, sharpness, and more.
 */

import { useState } from 'react';
import { Settings, ChevronDown, Zap, Hash, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

interface AdvancedSettingsProps {
  numImages: number;
  seed: number;
  guidanceScale: number;
  numSteps: number;
  sharpness: number;
  performance: 'Speed' | 'Quality' | 'Extreme Speed' | 'Lightning' | 'Hyper-SD';
  outputFormat: 'png' | 'jpg' | 'webp';
  onNumImagesChange: (value: number) => void;
  onSeedChange: (value: number) => void;
  onGuidanceScaleChange: (value: number) => void;
  onNumStepsChange: (value: number) => void;
  onSharpnessChange: (value: number) => void;
  onPerformanceChange: (
    value: 'Speed' | 'Quality' | 'Extreme Speed' | 'Lightning' | 'Hyper-SD'
  ) => void;
  onOutputFormatChange: (value: 'png' | 'jpg' | 'webp') => void;
  disabled?: boolean;
  /**
   * Whether the seed pins this provider's output. REQUIRED, and deliberately
   * not defaulted like the flags below it: the seed box promises "reproducible
   * results", and the six `openrouter-*` models accept a seed and ignore it.
   * A default of true is how a new provider inherits that promise silently.
   */
  showSeed: boolean;
  // Provider-specific feature flags
  showNumSteps?: boolean;
  showGuidanceScale?: boolean;
  showSharpness?: boolean;
  showPerformance?: boolean;
  showOutputFormat?: boolean;
  // Provider-specific limits
  maxNumSteps?: number;
  maxGuidanceScale?: number;
  // Per-model recommendations (e.g. Hyper checkpoints need ~6 steps at low CFG)
  recommendedSteps?: number;
  recommendedGuidanceScale?: number;
}

const PERFORMANCE_OPTIONS = [
  { value: 'Hyper-SD', label: 'Hyper-SD', description: 'Fast, best 4-step quality' },
  { value: 'Lightning', label: 'Lightning', description: 'Fastest (4 steps)' },
  { value: 'Extreme Speed', label: 'Extreme Speed', description: 'Very fast (8 steps)' },
  { value: 'Speed', label: 'Speed', description: 'Fast (30 steps)' },
  { value: 'Quality', label: 'Quality', description: 'Best quality (60 steps)' },
] as const;

const FORMAT_OPTIONS = [
  { value: 'png', label: 'PNG', description: 'Lossless, larger file' },
  { value: 'jpg', label: 'JPG', description: 'Compressed, smaller file' },
  { value: 'webp', label: 'WebP', description: 'Modern, balanced' },
] as const;

export function AdvancedSettings({
  numImages,
  seed,
  guidanceScale,
  numSteps,
  sharpness,
  performance,
  outputFormat,
  onNumImagesChange,
  onSeedChange,
  onGuidanceScaleChange,
  onNumStepsChange,
  onSharpnessChange,
  onPerformanceChange,
  onOutputFormatChange,
  disabled = false,
  showSeed,
  // Provider feature flags - default to true for backward compatibility
  showNumSteps = true,
  showGuidanceScale = true,
  showSharpness = true,
  showPerformance = true,
  showOutputFormat = true,
  // Provider-specific limits
  maxNumSteps = 100,
  maxGuidanceScale = 30,
  // Per-model recommendations
  recommendedSteps,
  recommendedGuidanceScale,
}: AdvancedSettingsProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="space-y-3">
      {/* Toggle Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
        disabled={disabled}
      >
        <Settings className="h-4 w-4 text-secondary-600" />
        <span className="font-medium text-secondary-700">
          {t('image.advanced.title', { defaultValue: 'Advanced Settings' })}
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 text-secondary-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Settings Panel */}
      {isExpanded && (
        <div className="space-y-6 p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-800 animate-in slide-in-from-top-2 duration-200">
          {/* Performance Preset - Only for providers that support it */}
          {showPerformance && (
            <div className="space-y-2">
              <label className="form-label flex items-center gap-2">
                <Zap className="h-4 w-4" />
                {t('image.advanced.performance', { defaultValue: 'Performance Mode' })}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PERFORMANCE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onPerformanceChange(option.value)}
                    className={`p-2 text-sm rounded-lg border transition-all ${
                      performance === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                        : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-300 dark:hover:border-secondary-500'
                    }`}
                    disabled={disabled}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-secondary-500 dark:text-secondary-400">
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Number of Images */}
          <div className="space-y-2">
            <label htmlFor="num-images" className="form-label flex items-center gap-2">
              <Hash className="h-4 w-4" />
              {t('image.advanced.numImages', { defaultValue: 'Number of Images' })}: {numImages}
            </label>
            <input
              id="num-images"
              type="range"
              min={1}
              max={4}
              step={1}
              value={numImages}
              onChange={(e) => onNumImagesChange(parseInt(e.target.value))}
              className="slider-blue-600"
              disabled={disabled}
            />
            <div className="slider-labels">
              <span>1</span>
              <span>4</span>
            </div>
          </div>

          {/* Guidance Scale - Only for providers that support it */}
          {showGuidanceScale && (
            <div className="space-y-2">
              <label htmlFor="guidance-scale" className="form-label">
                {t('image.advanced.guidanceScale', { defaultValue: 'Guidance Scale (CFG)' })}:{' '}
                {guidanceScale.toFixed(1)}
              </label>
              <input
                id="guidance-scale"
                type="range"
                min={1}
                max={maxGuidanceScale}
                step={0.5}
                value={guidanceScale}
                onChange={(e) => onGuidanceScaleChange(parseFloat(e.target.value))}
                className="slider-blue-600"
                disabled={disabled}
              />
              <div className="slider-labels">
                <span>1 (Creative)</span>
                <span>{maxGuidanceScale} (Precise)</span>
              </div>
            </div>
          )}

          {/* Inference Steps - hidden when a Performance preset governs the step
              count (Fooocus). The preset (Speed=30, Quality=60, Extreme Speed=8,
              Lightning/Hyper-SD=4) is the single source of truth there: the
              backend payload sends `performance_selection`, never `num_steps`, so
              a free-form steps slider would be a dead control. Other providers
              (comfyui-sd, flux-dev, …) expose num_steps and have no preset. */}
          {showNumSteps && !showPerformance && (
            <div className="space-y-2">
              <label htmlFor="num-steps" className="form-label">
                {t('image.advanced.steps', { defaultValue: 'Inference Steps' })}: {numSteps}
              </label>
              <input
                id="num-steps"
                type="range"
                min={1}
                max={maxNumSteps}
                step={1}
                value={numSteps}
                onChange={(e) => onNumStepsChange(parseInt(e.target.value))}
                className="slider-blue-600"
                disabled={disabled}
              />
              <div className="slider-labels">
                <span>1 (Fast)</span>
                <span>{maxNumSteps} (Detailed)</span>
              </div>
              {typeof recommendedSteps === 'number' && numSteps !== recommendedSteps && (
                <div className="flex items-center gap-2 text-xs text-amber-300/90">
                  <Sparkles className="h-3 w-3 flex-shrink-0" />
                  <span className="flex-1">
                    {t('image.advanced.recommendedSteps', {
                      defaultValue:
                        'This model is tuned for {{steps}} steps{{cfg}} — fewer steps, faster results.',
                      steps: recommendedSteps,
                      cfg:
                        typeof recommendedGuidanceScale === 'number'
                          ? ` at CFG ${recommendedGuidanceScale}`
                          : '',
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      onNumStepsChange(recommendedSteps);
                      if (typeof recommendedGuidanceScale === 'number') {
                        onGuidanceScaleChange(recommendedGuidanceScale);
                      }
                    }}
                    disabled={disabled}
                    className="px-2 py-1 rounded bg-amber-900/40 text-amber-200 hover:bg-amber-900/60 transition-colors flex-shrink-0"
                  >
                    {t('image.advanced.applyRecommended', { defaultValue: 'Apply' })}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Sharpness - Only for providers that support it */}
          {showSharpness && (
            <div className="space-y-2">
              <label htmlFor="sharpness" className="form-label flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                {t('image.advanced.sharpness', { defaultValue: 'Sharpness' })}:{' '}
                {sharpness.toFixed(1)}
              </label>
              <input
                id="sharpness"
                type="range"
                min={0}
                max={30}
                step={0.5}
                value={sharpness}
                onChange={(e) => onSharpnessChange(parseFloat(e.target.value))}
                className="slider-blue-600"
                disabled={disabled}
              />
              <div className="slider-labels">
                <span>0 (Soft)</span>
                <span>30 (Sharp)</span>
              </div>
            </div>
          )}

          {/* Seed — hidden where a seed does not pin the output. */}
          {showSeed && (
            <div className="space-y-2">
              <label htmlFor="seed" className="form-label">
                {t('image.advanced.seed', { defaultValue: 'Seed' })}
              </label>
              <div className="flex gap-2">
                <input
                  id="seed"
                  type="number"
                  min={-1}
                  value={seed}
                  onChange={(e) => onSeedChange(parseInt(e.target.value) || -1)}
                  placeholder="-1 for random"
                  className="input flex-1"
                  disabled={disabled}
                />
                <button
                  type="button"
                  onClick={() => onSeedChange(-1)}
                  className="px-3 py-2 text-sm bg-black/30 hover:bg-black/40 text-white/70 border border-white/10 rounded-lg transition-colors"
                  disabled={disabled}
                >
                  {t('image.advanced.randomize', { defaultValue: 'Random' })}
                </button>
              </div>
              <p className="text-xs text-secondary-500">
                {t('image.advanced.seedHint', {
                  defaultValue: 'Use -1 for random, or specify a seed for reproducible results',
                })}
              </p>
            </div>
          )}

          {/* Output Format - Only for providers whose backend payload honors output_format */}
          {showOutputFormat && (
            <div className="space-y-2">
              <label className="form-label">
                {t('image.advanced.format', { defaultValue: 'Output Format' })}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {FORMAT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onOutputFormatChange(option.value)}
                    className={`p-2 text-sm rounded-lg border transition-all ${
                      outputFormat === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-secondary-200 hover:border-secondary-300'
                    }`}
                    disabled={disabled}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-secondary-500">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdvancedSettings;
